/**
 * Travel VFX tuning (`reachFx.json`, M3 plan R10): the relay tunnel, cruise streaks, interdiction feedback and the
 * relay and lane-ring cores. Validated with Zod at load; the dev panel mutates the parsed object in place.
 */
import { z } from 'zod'
import reachFxJson from './reachFx.json'

const hex = z.string().regex(/^#[0-9A-Fa-f]{6}$/)
const positive = z.number().positive()
const nonNegative = z.number().nonnegative()
const unit = z.number().min(0).max(1)

const tunnelSchema = z
  .object({
    /** Inner radius of the open cylinder around the camera (m). */
    radiusM: positive,
    /** Cylinder length (m), centred on the camera. */
    lengthM: positive,
    radialSegments: z.number().int().min(8),
    /** Fade-in over the `enter` phase and fade-out over the `exit` phase (s). */
    fadeInS: positive,
    fadeOutS: positive,
    /** Wall opacity while the tunnel holds (hides the system switch behind it). */
    maxOpacity: unit,
    wallColor: hex,
    streakColor: hex,
    /** HDR multiplier on the streaks (bloom picks them up). */
    streakIntensity: nonNegative,
    /** Streak columns around the circumference. */
    streakColumns: z.number().int().min(1),
    /** Streak cells along the length. */
    streakRepeat: positive,
    /** Scroll rate in cells per second (towards the camera's back). */
    streakScroll: nonNegative,
    /** Streak thickness as a fraction of a column. */
    streakWidth: unit,
    /** Share of cells that hold a streak. */
    streakDensity: unit,
    pulseHz: nonNegative,
    pulseIntensity: nonNegative,
    /** Share of the length (at each end) over which the wall fades to its base colour. */
    endFade: z.number().min(0).max(0.5),
  })
  .strict()

const streaksSchema = z
  .object({
    count: z.number().int().min(1).max(4096),
    /** Segments recycle inside this sphere around the camera (m). */
    sphereRadiusM: positive,
    /** Streak length = clamp(speed × this, min, max). */
    lengthPerSpeedS: nonNegative,
    minLengthM: nonNegative,
    maxLengthM: positive,
    widthM: positive,
    /** The visual flow speed past the camera is capped here (m/s): beyond it every segment would respawn every frame. */
    maxFlowSpeed: positive,
    /** Alpha rises from 0 at `alphaSpeedMin` to `maxAlpha` at `alphaSpeedMax` (m/s). */
    alphaSpeedMin: nonNegative,
    alphaSpeedMax: positive,
    maxAlpha: unit,
    /** Share of the radius over which a segment fades out towards the sphere's edge. */
    edgeFade: z.number().min(0).max(1),
    color: hex,
    intensity: nonNegative,
    seed: z.number().int().nonnegative(),
  })
  .strict()
  .refine((s) => s.maxLengthM >= s.minLengthM, 'streaks.maxLengthM must be ≥ minLengthM')
  .refine((s) => s.maxLengthM < s.sphereRadiusM, 'streaks.maxLengthM must be below sphereRadiusM')
  .refine((s) => s.alphaSpeedMax > s.alphaSpeedMin, 'streaks.alphaSpeedMax must exceed alphaSpeedMin')

const interdictionSchema = z
  .object({
    /** Feedback (post edge) while interdicted, before the pull term. */
    feedback: unit,
    /** Added feedback per unit of the pull meter. */
    feedbackPull: unit,
    /** Camera jitter ceiling (degrees; the plan caps it at 0.6°). */
    jitterMaxDeg: z.number().min(0).max(0.6),
    /** Jitter share at zero pull (it grows to the full ceiling at pull 1). */
    jitterBase: unit,
    jitterHz: positive,
    /** Feedback flash on a cruise drop, and on a drop out of a failed interdiction. */
    dropFlash: unit,
    interdictedDropFlash: unit,
    dropFlashS: positive,
    seed: z.number().int().nonnegative(),
  })
  .strict()

const coreSchema = z
  .object({
    onlineColor: hex,
    onlineIntensity: nonNegative,
    offlineColor: hex,
    offlineIntensity: nonNegative,
    /** Flicker depth at the start of a reboot; it fades to 0 as the progress reaches 1. */
    rebootFlicker: unit,
    flickerHz: positive,
    swirlArms: z.number().int().min(1),
    swirlTwist: nonNegative,
    /** Swirl turns per second. */
    swirlSpeed: nonNegative,
    /** Share of the radius over which the disc fades out at the rim. */
    rimFade: z.number().min(0).max(1),
    /** Dust grain scale across the disc (offline). */
    dustScale: positive,
  })
  .strict()

export const reachFxConfigSchema = z
  .object({
    tunnel: tunnelSchema,
    streaks: streaksSchema,
    interdiction: interdictionSchema,
    relayCore: coreSchema,
    laneCore: coreSchema,
    /** The dev-only preview toggles (dev panel "Reach FX"). */
    devPreview: z
      .object({
        coreDistanceM: positive,
        coreApertureM: positive,
        /** Seconds per preview state (online, offline, reboot). */
        coreStateS: positive,
        /** The speed the streak preview pretends to fly at (m/s). */
        streakSpeed: positive,
      })
      .strict(),
  })
  .strict()

export type ReachFxConfig = z.infer<typeof reachFxConfigSchema>
export type TunnelConfig = ReachFxConfig['tunnel']
export type StreaksConfig = ReachFxConfig['streaks']
export type InterdictionFxConfig = ReachFxConfig['interdiction']
export type CoreConfig = ReachFxConfig['relayCore']

/** Parses `reachFx.json` (or `raw`). Throws a ZodError naming the bad field. */
export function loadReachFxConfig(raw: unknown = reachFxJson): ReachFxConfig {
  return reachFxConfigSchema.parse(raw)
}
